import AuthenticatorMethodRadioButton from '../../../components/enroll-phone/oie/AuthenticatorMethodRadioButton';
import AmplitudeUtils from '../../../utils/amplitude-utils';
import { isAnetUser } from '../../../utils/user-utils';
import {
  getCurrentAuthenticatorMethodForPhone,
  getEnrolledPhoneFactorMethods,
  getOktaWidgetModel,
  navigateToTerminalViewWithError
} from '../../../utils/widget-facade';
import BaseEnrollPhoneFactorCustomizer from '../base';
import athenaConfig from '../../../config';
import { PhoneEnrollmentLimitExceededError } from '../../../utils/errors';
import { PHONE_FACTOR_ENROLLMENT_EVENT_NAME } from '../../../constants';

export default class AuthenticatorEnrollmentDataCustomizer extends BaseEnrollPhoneFactorCustomizer {
  primaryButtonOnClick = () => {
    const authenticatorMethodType = getOktaWidgetModel().get('authenticator.methodType');
    const amplitudeEvent = authenticatorMethodType === 'voice' ? 'VoiceCall::CallButtonClick' : 'SMS::SendCodeButtonClick';
    AmplitudeUtils.logMfaEnrollEvent(amplitudeEvent);
  };

  private isLandingCompleted: boolean = false;

  async shouldCustomize() {
    return [ 'authenticator-enrollment-data' ].includes(this.context?.formName);
  }

  async switchVisibleMethod({ existingMethodType }: { existingMethodType: string }) {
    if (existingMethodType === 'sms') {
      const callElement = jQueryCourage('.radio-label:contains("Voice call")');
      callElement.trigger('click');
    }
    // This should not occur in reality since Okta always defaults to sms as the visible method.
    else if (existingMethodType === 'call') {
      const smsElement = jQueryCourage('.radio-label:contains("Text message (SMS)")');
      smsElement.trigger('click');
    }
  }

  async updatePhoneEnrollmentMethod({ enrolledMethodForPhoneFactor }: { enrolledMethodForPhoneFactor: string }) {
    const searchParams = new URLSearchParams(window.location.search);
    const methodTypeToDisplay = searchParams.get('factorType');
    const visibleMethodType = getCurrentAuthenticatorMethodForPhone();
    // This is to prevent double enrollment of same factor from end user settings
    if (visibleMethodType === enrolledMethodForPhoneFactor) {
      await this.switchVisibleMethod({ existingMethodType: enrolledMethodForPhoneFactor });
      this.hidePhoneEnrollmentRadioButtons();
    }
    else if (methodTypeToDisplay) {
      // This indicates that the method which user wants to enroll with is already enrolled
      // So we switch to other available method
      if (methodTypeToDisplay === enrolledMethodForPhoneFactor) {
        await this.switchVisibleMethod({ existingMethodType: enrolledMethodForPhoneFactor });
      }
      // By default, the visible method is always sms.
      // If we want to switch to call based on parameter sent from lwa-upm, this will be used.
      else if (methodTypeToDisplay !== visibleMethodType) {
        await this.switchVisibleMethod({ existingMethodType: visibleMethodType });
      }
      this.hidePhoneEnrollmentRadioButtons();
    }
  }

  hidePhoneEnrollmentRadioButtons() {
    this.eventEmitter.emitEventToComponent({
      componentName: AuthenticatorMethodRadioButton.displayName as string,
      eventName: 'hide-authenticator-method-radio-button',
    });
  }

  async customizeToLimitPhoneEnrollments() {
    const enrolledPhoneFactorMethods = getEnrolledPhoneFactorMethods();
    if (enrolledPhoneFactorMethods) {
      if (enrolledPhoneFactorMethods.length >= 2) {
        AmplitudeUtils.logCustomEvent(PHONE_FACTOR_ENROLLMENT_EVENT_NAME, { enrolledPhoneFactorMethods, workflow: 'MFAEnroll' });
        navigateToTerminalViewWithError('oie.phone.limit.exceeded.error');
        // navigateToTerminalViewWithError first updates the errorMessage in Okta's model, which causes the
        // o-form-error element to get populated, while the controller name is not updated. By default, we
        // will continue to finish all operations for current controller, and then Okta will change
        // the controller to terminal. To avoid further customizations, we throw an error and return.
        throw new PhoneEnrollmentLimitExceededError();
      }
      else if (enrolledPhoneFactorMethods?.includes('unknown')) {
        AmplitudeUtils.logCustomEvent('InvalidPhoneFactor', { enrolledPhoneFactorMethods });
      }
      else {
        const enrolledMethodForPhoneFactor = enrolledPhoneFactorMethods?.length === 1 ? enrolledPhoneFactorMethods[0] : 'none';
        await this.updatePhoneEnrollmentMethod({ enrolledMethodForPhoneFactor });
      }
    }
  }

  async customize() {
    if (athenaConfig.limitPhoneEnrollmentsInOIE) {
      const shouldLimitPhoneEnrollments = !isAnetUser();
      if (shouldLimitPhoneEnrollments) {
        try {
          await this.customizeToLimitPhoneEnrollments();
        }
        catch (error) {
          if (error instanceof PhoneEnrollmentLimitExceededError) {
            return;
          }
          throw error;
        }
      }
    }
    await super.customize();
  }

  protected forgifyController() {
    if (!this.isLandingCompleted) {
      AmplitudeUtils.logMfaEnrollEvent('Phone::Landing');
      this.isLandingCompleted = true;
    }
    super.forgifyController();
    this.onMethodTypeChange();
  }

  private onMethodTypeChange() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    const widgetModel = getOktaWidgetModel();
    widgetModel.once('change:authenticator.methodType', async function () {
      await self.customize();
    });
  }
}
